import { el, setChildren } from 'redom';
import { Node } from 'slate';
import { ReactEditor } from 'slate-react';

import application from 'application';

import { stringToSVG } from 'utilities';
import {
  convertToOptionsCollection
} from 'utilities/collection';

import SimpleMenu from 'generics/components/simple-menu';

import { getShapeOptions, shapeTypes } from './utilities';

import './index.scss';

import rectangleIconString from 'assets/images/rectangle-icon.svg?raw';
import circleIconString from 'assets/images/circle-icon.svg?raw';
import lineIconString from 'assets/images/line-icon.svg?raw';
import polygonIconString from 'assets/images/polygon-icon.svg?raw';
import ellipseIconString from 'assets/images/ellipse-icon.svg?raw';
import triangleIconString from 'assets/images/triangle-icon.svg?raw';
import textIconString from 'assets/images/text-icon.svg?raw';
import pathIconString from 'assets/images/path-icon.svg?raw';
import imageIconString from 'assets/images/image-icon.svg?raw';
import videoIconString from 'assets/images/video-icon.svg?raw';
import audioIconString from 'assets/images/audio-icon.svg?raw';

const audioIcon = stringToSVG(audioIconString);
const videoIcon = stringToSVG(videoIconString);
const imageIcon = stringToSVG(imageIconString);
const pathIcon = stringToSVG(pathIconString);
const triangleIcon = stringToSVG(triangleIconString);
const ellipseIcon = stringToSVG(ellipseIconString);
const polygonIcon = stringToSVG(polygonIconString);
const lineIcon = stringToSVG(lineIconString);
const circleIcon = stringToSVG(circleIconString);
const rectangleIcon = stringToSVG(rectangleIconString);
const textIcon = stringToSVG(textIconString);

class ShapesMenuPrimaryView {

  constructor(name) {

    this.el = el('p.shape-options-button-primary-view', [
      circleIcon.cloneNode(true),
      el('span', name)
    ]);

  }

}

class ItemView {

  constructor(list) {

    this.list = list;
    this.option = null;
    this.label = el('span.menu-option-label');
    this.el = el('span.menu-option-item', {
      onclick: this.handleClick.bind(this)
    });

  }

  handleClick(event) {

    event.stopPropagation();
    event.preventDefault();

    return this.list.onSelect(event, this.option);

  }

  update(option) {

    this.option = option;

    this.label.textContent = option.label;

    const children = [this.label];

    if (this.option.value === shapeTypes.RECTANGLE) {
      children.unshift(rectangleIcon.cloneNode(true));
    } else if (this.option.value === shapeTypes.CIRCLE) {
      children.unshift(circleIcon.cloneNode(true));
    } else if (this.option.value === shapeTypes.LINE) {
      children.unshift(lineIcon.cloneNode(true));
    } else if (this.option.value === shapeTypes.POLYGON) {
      children.unshift(polygonIcon.cloneNode(true));
    } else if (this.option.value === shapeTypes.ELLIPSE) {
      children.unshift(ellipseIcon.cloneNode(true));
    } else if (this.option.value === shapeTypes.TRIANGLE) {
      children.unshift(triangleIcon.cloneNode(true));
    } else if (this.option.value === shapeTypes.PATH) {
      children.unshift(pathIcon.cloneNode(true));
    } else if (this.option.value === shapeTypes.TEXT) {
      children.unshift(textIcon.cloneNode(true));
    } else if (this.option.value === shapeTypes.IMAGE) {
      children.unshift(imageIcon.cloneNode(true));
    } else if (this.option.value === shapeTypes.VIDEO) {
      children.unshift(videoIcon.cloneNode(true));
    } else if (this.option.value === shapeTypes.AUDIO) {
      children.unshift(audioIcon.cloneNode(true));
    }

    setChildren(this.el, children);

  }

}

class CanvasShapesPanel {

  constructor(configuration) {

    this.editor = configuration.editor;
    this.activeNodePath = configuration.activeNodePath;
    this.videoCanvas = configuration.videoCanvas || false;

    this.menu = new SimpleMenu({
      id: 'canvas-shapes-menu',
      name: 'Shapes',
      PrimaryContentView: ShapesMenuPrimaryView,
      optionsDisplaySide: 'right',
      options: convertToOptionsCollection(getShapeOptions(this.videoCanvas), 'name', 'type'),
      ItemView
    });

    this.menu.registerClickEvent(this.handleTransistionSelect.bind(this));

    this.el = el('button#shape-options-button', [this.menu]);

  }

  handleTransistionSelect(event, option) {

    const object = option.object;

    if (object.type === shapeTypes.IMAGE) {
      this.insertFile(event);
    } else if (object.type === shapeTypes.VIDEO) {
      this.insertVideo(event);
    } else if (object.type === shapeTypes.AUDIO) {
      this.insertAudio(event);
    } else {
      this.insertShape(event, object.type);
    }

  }

  insertShape(event, type) {

    event.stopPropagation();

    const canvasNode = Node.get(this.editor, this.activeNodePath);
    const domNode = ReactEditor.toDOMNode(this.editor, canvasNode);

    domNode.querySelector('.canvas-container').canvas.insertShape(type);

  }

  async insertFile(event) {

    event.stopPropagation();

    const result = await application.domUtility.askImageFile();

    if (result.cancelled === true) {
      return;
    }

    const canvasNode = Node.get(this.editor, this.activeNodePath);
    const domNode = ReactEditor.toDOMNode(this.editor, canvasNode);

    domNode.querySelector('.canvas-container').canvas.insertImage(result.file);

  }

  async insertVideo(event) {

    event.stopPropagation();

    const result = await application.domUtility.askVideoFile();

    if (result.cancelled === true) {
      return;
    }

    const canvasNode = Node.get(this.editor, this.activeNodePath);
    const domNode = ReactEditor.toDOMNode(this.editor, canvasNode);

    domNode.querySelector('.canvas-container').canvas.insertVideo(result.file);

  }

  async insertAudio(event) {

    event.stopPropagation();

    const result = await application.domUtility.askAudioFile();

    if (result.cancelled === true) {
      return;
    }

    const canvasNode = Node.get(this.editor, this.activeNodePath);
    const domNode = ReactEditor.toDOMNode(this.editor, canvasNode);

    domNode.querySelector('.canvas-container').canvas.insertAudio(result.file);

  }

  update(configuration) {

    this.editor = configuration.editor;
    this.activeNodePath = configuration.activeNodePath;

  }

}

export default CanvasShapesPanel;